import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaBookOpen, FaExternalLinkAlt, FaGithub, FaSearch, FaTable, FaTimes, FaYoutube } from "react-icons/fa";
import { useDrop } from "../contexts/DropContext";
import projects from "../data/projects.json";
import projectArtwork from "../data/projectArtwork";
import { loadProjectCounts } from "../data/publicSessions";
import { getProjectWebinar } from "../data/projectWebinars";
import { getProjectGroups, getStimulusCode } from "../data/projectCatalogue";
import "./Dropdown.css";

export function getDatabookUrl(project) {
  if (project.databookUrl === null) return null;
  return project.databookUrl || `https://alleninstitute.github.io/openscope_databook/${project.id.replace(/_/g, "-")}/`;
}

function countLabel(counts) {
  if (!counts) return "Loading counts...";
  if (counts.error) return "Counts unavailable";
  const mice = counts.mice === null ? "Mouse count unavailable" : `${counts.mice} ${counts.mice === 1 ? "mouse" : "mice"}`;
  return `${mice} / ${counts.sessions} ${counts.sessions === 1 ? "session" : "sessions"}`;
}

export default function Dropdown({ notebooks = false }) {
  const [search, setSearch] = useState("");
  const [projectCounts, setProjectCounts] = useState({});
  const { setOption } = useDrop();
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    projects.forEach(project => {
      loadProjectCounts(project.id, controller.signal).then(counts => {
        if (active) setProjectCounts(previous => ({ ...previous, [project.id]: counts }));
      }).catch(error => {
        if (active && error.name !== "AbortError") {
          setProjectCounts(previous => ({ ...previous, [project.id]: { error: true } }));
        }
      });
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  const query = search.trim().toLowerCase();
  const projectGroups = getProjectGroups();
  const visibleProjects = projectGroups.filter(group =>
    `${group.name} ${group.summary} ${group.datasets.map(dataset => `${dataset.name} ${dataset.summary} ${dataset.dandiset} ${dataset.firebaseProject || ""}`).join(" ")}`.toLowerCase().includes(query)
  );
  const allCountsLoaded = projects.every(project => projectCounts[project.id] && !projectCounts[project.id].error);
  const totalSessions = allCountsLoaded
    ? projects.reduce((total, project) => total + projectCounts[project.id].sessions, 0) : null;
  const countsFailed = projects.some(project => projectCounts[project.id]?.error);

  function renderDatasetCounts(dataset) {
    return <div className="project-meta">
      <span className="project-counts" title="Distinct mice and public sessions in this dataset">{countLabel(projectCounts[dataset.id])}</span>
      <span className="project-identifier">DANDI {dataset.dandiset}</span>
    </div>;
  }

  function renderDatasetLinks(dataset) {
    const webinar = getProjectWebinar(dataset);
    const databookUrl = getDatabookUrl(dataset);
    const stimulusCode = getStimulusCode(dataset);
    return <div className="project-links">
      {databookUrl && <a href={databookUrl} target="_blank" rel="noopener noreferrer" aria-label={`${dataset.name} Databook`}>
        <FaBookOpen aria-hidden="true" /> Databook
      </a>}
      <a href={`https://dandiarchive.org/dandiset/${dataset.dandiset}`} target="_blank" rel="noopener noreferrer" aria-label={`${dataset.name} DANDI dataset`}>
        <FaExternalLinkAlt aria-hidden="true" /> DANDI
      </a>
      {stimulusCode && <a href={stimulusCode.url} target="_blank" rel="noopener noreferrer"
        aria-label={`${dataset.name} stimulus code`} title={stimulusCode.detail || stimulusCode.title}>
        <FaGithub aria-hidden="true" /> Stimulus code
      </a>}
      {webinar && <a href={webinar.watchUrl} target="_blank" rel="noopener noreferrer"
        aria-label={`${dataset.name} presentation on YouTube`} title={webinar.title}>
        <FaYoutube aria-hidden="true" /> {webinar.label}
      </a>}
      {!notebooks && <Link
        to={dataset.firebaseProject ? `/table?project=${encodeURIComponent(dataset.firebaseProject)}` : `/projects/${dataset.id}#public-sessions`}
        onClick={() => dataset.firebaseProject && setOption(dataset.firebaseProject)}
        aria-label={`${dataset.name} sessions`} className="project-sessions">
        <FaTable aria-hidden="true" /> Sessions
      </Link>}
    </div>;
  }

  return (
    <main className="project-catalogue">
      <div className="catalogue-heading">
        <div>
          <p className="section-eyebrow">Neuroscience / Open data</p>
          <h2>{notebooks ? "Project notebooks" : "OpenScope projects"}</h2>
        </div>
        <dl className="catalogue-metrics">
          <div><dt>Projects</dt><dd>{projectGroups.length}</dd></div>
          <div><dt>Datasets</dt><dd>{projects.length}</dd></div>
          <div><dt>Public sessions</dt><dd>{countsFailed ? 'N/A' : totalSessions === null ? '...' : totalSessions.toLocaleString()}</dd></div>
        </dl>
      </div>
      <div className="catalogue-toolbar">
        <div className="catalogue-search">
          <FaSearch aria-hidden="true" />
          <input
            type="search"
            aria-label="Search projects"
            placeholder="Search projects or DANDI ID"
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
          {search && (
            <button type="button" aria-label="Clear search" title="Clear search" onClick={() => setSearch("")}>
              <FaTimes aria-hidden="true" />
            </button>
          )}
        </div>
        <span className="catalogue-count" role="status">
          {visibleProjects.length} of {projectGroups.length} projects
        </span>
      </div>
      <div className="project-list">
        {visibleProjects.map(group => {
          const firstDataset = group.datasets[0];
          const multipleDatasets = group.datasets.length > 1;
          const artwork = projectArtwork[firstDataset.id];
          return (
          <article className={`project-row${multipleDatasets ? " project-grouped" : ""}`} key={group.id} aria-labelledby={`project-${group.id}`}>
            <Link to={`/projects/${firstDataset.id}`} className="project-artwork" aria-label={`Open ${group.name} project`}>
              <img src={artwork.thumbnail} alt={artwork.thumbnailAlt || artwork.alt} title={artwork.thumbnailAlt || artwork.alt} />
            </Link>
            <div className="project-identity">
              <h3 id={`project-${group.id}`}><Link to={`/projects/${firstDataset.id}`}>{group.name}</Link></h3>
              <p className="project-summary">{group.summary}</p>
              {multipleDatasets ? <span className="project-dataset-count">{group.datasets.length} datasets</span> : renderDatasetCounts(firstDataset)}
            </div>
            {multipleDatasets ? <div className="project-datasets">
              {group.datasets.map(dataset => <section className="project-dataset" key={dataset.id} aria-label={`${dataset.name} dataset`}>
                <div>
                  <h4><Link to={`/projects/${dataset.id}`} aria-label={dataset.name}>{dataset.modality}</Link></h4>
                  {renderDatasetCounts(dataset)}
                </div>
                {renderDatasetLinks(dataset)}
              </section>)}
            </div> : renderDatasetLinks(firstDataset)}
          </article>
          );
        })}
      </div>
      {visibleProjects.length === 0 && <p className="catalogue-empty">No projects match your search.</p>}
    </main>
  );
}